import md5 from 'blueimp-md5'
import { z } from 'zod'
import { ApiError } from './client'
import type { ApiClient } from './client'
import { accessDetailsResponse } from './access'
import type { AccessDetails } from './access'

/**
 * Auth endpoints.
 *
 * VERIFIED 2026-08-20 against the primary console's own deployed bundle at
 * ap1.mira.bloo.io — all 196 files, including every one of its 193 lazy chunks.
 * Full extract: docs/inventory/LEGACY-LOGIN-DECOMPILED.md. This is no longer a
 * transcription; it is what the shipped app actually does.
 *
 * The credential scheme:
 *
 *     cstPass = md5(chId + md5(password))     // lowercase hex
 *
 * There is NO challenge-response, despite the two-step email → password screen
 * suggesting one. In the deployed login component `chId` is the literal '0612'
 * in initial state, read once to build cstPass, and never assigned from any
 * server response — there is no setState({chId}) anywhere in the app. The
 * second screen only flips a local boolean behind a local regex; no request is
 * made between the email step and the password step. /lc/users/init has zero
 * call sites.
 *
 * SECURITY FINDING (A4, docs/inventory/FINDINGS.md): the transform is therefore
 * static. Anyone with the request can replay it, and md5(password) is not a
 * defensible at-rest form. It adds nothing over TLS. Backend recommendation:
 * real challenge-response, or drop the transform and rely on TLS + a
 * server-side KDF.
 *
 * We keep the chId parameter so that if /lc/users/init ever starts issuing real
 * per-session challenges, this client already takes one.
 */

export const LEGACY_CH_ID = '0612'

/**
 * Trim only — deliberately NOT lowercase.
 *
 * The console passes the raw field value, so a trailing space from autofill
 * would otherwise reach the backend and come back as "invalid credentials" for
 * a perfectly correct password. Trimming that is unambiguously safe.
 *
 * Lowercasing is NOT safe without evidence: if a tenant stores a mixed-case
 * address and matches it exactly, folding the case would cause the very failure
 * this function exists to prevent. The legacy client is no longer on disk, so
 * there is nothing to confirm what it sent. Leave the case alone until a
 * working tenant says otherwise.
 */
function normalizeEmail(emailId: string): string {
  return emailId.trim()
}

/**
 * Credential phrasing lives HERE, not in the HTTP core: this is the only place
 * that knows the failed request was a credential check. Applied only when the
 * server sent no message of its own -- a real server message always wins.
 */
function asCredentialError(e: unknown): never {
  if (e instanceof ApiError && e.serverMessage === undefined && (e.status === 400 || e.status === 401)) {
    throw new ApiError(e.status, 'Invalid email or password.', e.body)
  }
  throw e
}

export function derivePassword(password: string, chId: string = LEGACY_CH_ID): string {
  return md5(chId + md5(password))
}

/** Envelope: every auth response nests under data. Extra fields pass through. */
const envelope = <T extends z.ZodTypeAny>(inner: T) => z.object({ data: inner })

export const loginResponse = envelope(
  z
    .object({
      SSID: z.string().optional(),
      mfa_flag: z.enum(['enabled', 'disabled']).optional(),
      userType: z.string().optional(),
    })
    .passthrough(),
)
export type LoginResponse = z.infer<typeof loginResponse>['data']

export const samlResponse = envelope(z.object({ redirect_url: z.string() }).passthrough())

export interface AuthApi {
  /** POST /lc/users/init — the deployed console defines it and never calls it (zero call sites); kept for the day it matters. */
  init: (emailId: string) => Promise<unknown>
  login: (emailId: string, password: string, chId?: string) => Promise<LoginResponse>
  verifyOtp: (emailId: string, otp: string) => Promise<LoginResponse>
  samlCheck: (emailId: string) => Promise<string>
  logout: (ssid: string) => Promise<void>
  /** GET /lc/users/access-details — identity, orgs, scopes, entitlements. */
  accessDetails: () => Promise<AccessDetails>
}

export function createAuthApi(client: ApiClient): AuthApi {
  return {
    init: (emailId) =>
      client.request('POST', '/lc/users/init', {
        body: { emailId: normalizeEmail(emailId) },
        auth: false,
      }),

    login: async (emailId, password, chId) => {
      try {
        const res = await client.request('POST', '/lc/users/login', {
          body: { emailId: normalizeEmail(emailId), cstPass: derivePassword(password, chId) },
          schema: loginResponse,
          auth: false,
        })
        return res.data
      } catch (e) {
        return asCredentialError(e)
      }
    },

    verifyOtp: async (emailId, otp) => {
      // No `schema` here on purpose. The ap1 tenant answers this endpoint with
      // `200` and a literal `null` body, which a schema parse turns into a raw
      // "Response shape mismatch for /lc/users/verify-otp: ..." dump in the
      // user's face. Parse by hand so a missing body reads as a rejected code.
      let raw: unknown
      try {
        raw = await client.request<unknown>('POST', '/lc/users/verify-otp', {
          // Field name is OTP, capitalized — the backend is case-sensitive here.
          body: { emailId: normalizeEmail(emailId), OTP: otp },
          auth: false,
        })
      } catch (e) {
        // A message-less 400/401 here is about the CODE — the shared fallback
        // would tell a user who has no session yet to "sign in again".
        if (e instanceof ApiError && e.serverMessage === undefined && (e.status === 400 || e.status === 401)) {
          throw new ApiError(e.status, 'That code was not accepted. Request a new one.', e.body)
        }
        throw e
      }
      const parsed = loginResponse.safeParse(raw)
      if (!parsed.success) {
        // The envelope may carry the server's own reason ("OTP expired, a new
        // code was sent") — always better than the generic line.
        const msg =
          typeof raw === 'object' && raw !== null && typeof (raw as { message?: unknown }).message === 'string'
            ? (raw as { message: string }).message
            : 'That code was not accepted. Request a new one.'
        throw new ApiError(200, msg, raw)
      }
      return parsed.data.data
    },

    samlCheck: async (emailId) => {
      const res = await client.request('POST', '/lc/users/saml/login', {
        body: { emailId: normalizeEmail(emailId) },
        schema: samlResponse,
        auth: false,
      })
      return res.data.redirect_url
    },

    logout: async (ssid) => {
      await client.request('POST', '/lc/users/logout', { body: { ssid } })
    },

    accessDetails: async () => {
      const res = await client.request('GET', '/lc/users/access-details', {
        schema: accessDetailsResponse,
      })
      return res.data
    },
  }
}
